/**
 * Teach it once (#1738): the office's rules, from the client.
 *
 * A rule is attached to a customer, a site, a water authority or a job type,
 * and every matching job carries it as a Finish Line promise. The server holds
 * the rules (`/api/promise-rules`) and the per-job marks
 * (`/api/jobs/:id/promise-checks`); the engine turns them into promises. This
 * file is the reads, the writes, and the words, each word with a test.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { DeliveryRoute, JobPromise, Phase, PromiseRuleMeta, RuleKind, RuleScope } from "@/lib/finish-line";

// ── Shapes (mirror src/server/services/promiseRules.ts) ─────────────────────

export interface RuleTarget {
    scopeType: RuleScope;
    scopeId: string;
    name: string;
    openJobs: number;
}

export interface RuleTargets {
    jobId: string;
    jobNumber: string | null;
    vendorWo: boolean;
    targets: RuleTarget[];
}

export interface RuleConfig {
    route?: DeliveryRoute;
    label?: string;
    blocksBooking?: boolean;
}

export interface PromiseRule {
    id: string;
    scopeType: RuleScope;
    scopeId: string;
    scopeName: string | null;
    kind: RuleKind;
    phase: Phase;
    owner: "office" | "tech";
    title: string;
    detail: string | null;
    config: RuleConfig;
    sourceJobId: string | null;
    sourceJobNumber: string | null;
    taughtBy: string | null;
    taughtAt: string;
    active: boolean;
}

export interface NewRule {
    scopeType: RuleScope;
    scopeId: string;
    kind: RuleKind;
    phase: Phase;
    owner: "office" | "tech";
    title: string;
    detail: string | null;
    config: RuleConfig;
    sourceJobId: string | null;
}

// ── Words ───────────────────────────────────────────────────────────────────

const SCOPE_LEAD: Record<RuleScope, string> = {
    customer: "this customer",
    site: "this site",
    water_authority: "this water authority",
    job_type: "this job type",
};

/** The radio's lead words ("this customer"). */
export function scopeLead(scope: RuleScope): string {
    return SCOPE_LEAD[scope];
}

const plural = (n: number, one: string, many = `${one}s`) => (n === 1 ? one : many);

/** "Applies to 14 open FWISD jobs" — said on the confirm, before anything is saved. */
export function appliesToWords(t: Pick<RuleTarget, "scopeType" | "name" | "openJobs">): string {
    const n = t.openJobs;
    if (n === 0) return "No open jobs match right now. It applies to every new one.";
    switch (t.scopeType) {
        case "customer":
        case "job_type":
            return `Applies to ${n} open ${t.name} ${plural(n, "job")}`;
        case "site":
            return `Applies to ${n} open ${plural(n, "job")} at ${t.name}`;
        case "water_authority":
            return `Applies to ${n} open ${plural(n, "job")} filed with ${t.name}`;
    }
}

function shortDay(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "America/Chicago" });
}

/** "Taught by Darcy, Oct 2, from job #082678091". */
export function taughtWords(r: Pick<PromiseRuleMeta, "taughtBy" | "taughtAt" | "sourceJobNumber">): string {
    const who = r.taughtBy ? r.taughtBy.split(" ")[0] : null;
    const parts = [who ? `Taught by ${who}` : "Taught", shortDay(r.taughtAt)].filter(Boolean);
    return `${parts.join(", ")}${r.sourceJobNumber ? `, from job #${r.sourceJobNumber}` : ""}`;
}

export const ROUTE_CHOICES: { route: DeliveryRoute; label: string; placeholder: string }[] = [
    { route: "portal", label: "Their vendor portal", placeholder: "FWISD vendor portal" },
    { route: "mail", label: "Mail", placeholder: "Accounts payable, 100 N University Dr" },
    { route: "contact", label: "A specific person", placeholder: "Rafael Ordonez" },
];

/** The title a rule gets when the office doesn't write one. */
export function defaultTitle(kind: RuleKind, scopeName: string, route: DeliveryRoute): string {
    if (kind === "delivery_route") {
        const how = route === "mail" ? "mail" : route === "contact" ? "a named contact" : "portal";
        return `${scopeName}: deliver by ${how}`;
    }
    if (kind === "stamp_required") return `${scopeName}: stamp required`;
    return "";
}

/**
 * "Make this a rule" — where a promise row offers it. After the office works
 * around a bounced packet (the delivery truth fired), or on a vendor WO's
 * stamp. Never on a promise a rule already shapes.
 */
export function ruleSuggestion(p: JobPromise): RuleKind | null {
    if (p.rule || p.state === "not_applicable") return null;
    if (p.id === "packet_delivered" && (p.truthIds?.length ?? 0) > 0) return "delivery_route";
    if (p.id === "store_stamp") return "stamp_required";
    return null;
}

// ── Reads and writes ────────────────────────────────────────────────────────

export const ruleTargetsKey = (jobId: string) => ["job", jobId, "promise-rule-targets"] as const;
export const customerRulesKey = (customerId: string) => ["promise-rules", "customer", customerId] as const;

export function useRuleTargets(jobId: string, enabled: boolean) {
    return useQuery<RuleTargets>({
        queryKey: ruleTargetsKey(jobId),
        queryFn: () => api.get<RuleTargets>(`/api/jobs/${jobId}/promise-rules/targets`),
        enabled,
        staleTime: 30_000,
        retry: false,
    });
}

export function useCustomerRules(customerId: string | undefined, enabled = true) {
    return useQuery<PromiseRule[]>({
        queryKey: customerRulesKey(customerId ?? ""),
        queryFn: async () =>
            (await api.get<{ rules: PromiseRule[] }>(`/api/promise-rules?customerId=${customerId}`)).rules,
        enabled: !!customerId && enabled,
        staleTime: 15_000,
        retry: false,
    });
}

/** A rule touches every matching job, so re-read every job's Finish Line and brief, and the rules lists. */
function invalidateRules(qc: ReturnType<typeof useQueryClient>) {
    void qc.invalidateQueries({ queryKey: ["job"] });
    void qc.invalidateQueries({ queryKey: ["promise-rules"] });
}

export function useCreateRule() {
    const qc = useQueryClient();
    return useMutation<{ rule: PromiseRule; openJobs: number }, Error, NewRule>({
        mutationFn: (body) => api.post<{ rule: PromiseRule; openJobs: number }>("/api/promise-rules", body),
        onSuccess: () => invalidateRules(qc),
    });
}

export function useRetireRule() {
    const qc = useQueryClient();
    return useMutation<{ rule: PromiseRule }, Error, string>({
        mutationFn: (ruleId) => api.post<{ rule: PromiseRule }>(`/api/promise-rules/${ruleId}/retire`),
        onSuccess: () => invalidateRules(qc),
    });
}

/** Mark a rule kept on this job (with an optional note), or undo it. */
export function useRuleCheck(jobId: string) {
    const qc = useQueryClient();
    const done = () => void qc.invalidateQueries({ queryKey: ["job", jobId] });
    const mark = useMutation<{ ok: true; at: string }, Error, { ruleId: string; note: string | null }>({
        mutationFn: ({ ruleId, note }) =>
            api.post<{ ok: true; at: string }>(`/api/jobs/${jobId}/promise-checks`, { ruleId, note }),
        onSuccess: done,
    });
    const undo = useMutation<{ ok: true }, Error, string>({
        mutationFn: (ruleId) => api.delete<{ ok: true }>(`/api/jobs/${jobId}/promise-checks/${ruleId}`),
        onSuccess: done,
    });
    return { mark, undo };
}
